/* Pocket Factory · P2-LINKCHECK-1: the one link checker, window.PFLink.
   - The Buy gate's link rules, moved here from assets/pocket.js unchanged: safeLink (safe), reviewUrl, LINKED, linkGate
     (gate), the review card's website fallback (LINK-FALLBACK-1: fallback), Google's listing link (listing) and the
     words the gate says for a missing link (words). assets/pocket.js uses them whenever this script is on the page; it
     keeps its own copy only until a layout loads this script before it (the same rules: tests/p2-linkcheck-1.test.cjs).
   - check(url, purpose, ctx): the gate's answer for one card's link, {ok, url, reason, words, opensWebsite}.
   - The "Check the link" part (snippets/pf-link-check.liquid, assets/pf-link-check.css): what a tap opens, the whole link
     as the order line carries it, Test link (a new tab), a QR to scan from 1024px (assets/pf-qr.js, static) and the
     cards' lock; with no link the gate takes, the gate's own words and "Add the link your cards open", which asks the
     host to take the shopper there (pf:link-fix {purpose, reason, source}). Each block is drawn from its data-* when
     this script finds it; a host changes one with PFLink.render(block, {url, purpose, ...}).
   Events, as pocket-cro.js sends its pfx_* ones: review_link_tested {purpose, method: open | qr, source} (Test link, or
   the QR on screen for 2 s) and review_link_fixed {purpose, source} (a block without a link the gate takes, given one).
   Node (module.exports) gets the same object, for tests. */
(function (root, factory) {
  'use strict';
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document && !root.PFLink) {
    root.PFLink = api;
    api.start(root.document);
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';
  const brandService = () => root.PocketBrandService;
  const core = () => root.PocketCore;

  /* ---------- The Buy gate's link rules (assets/pocket.js, unchanged) ---------- */
  /** A link a card can open, as the order line carries it; '' when it is not one (pocket.js safeLink). */
  function safe(input) { try { return brandService().normalizeDestination(input); } catch (_) { return ''; } }
  // Reviews: the card ships locked to a permanent link, a link to their review page. Without one Buy goes on all the
  // same, marked review_link_needed (R0, Oct 25). Same rules as the review link check in pocket-reviews-v2.js (the hosts
  // are PocketCore.REVIEW_HOSTS).
  /** A review page link for the review site (pocket.js reviewUrl); '' when it is not one. */
  function reviewUrl(input, platform) {
    try {
      const raw = String(input || '').trim();
      if (!raw || raw.length > 2048 || /[\s\\\x00-\x1f]/.test(raw)) return '';
      // A link pasted without https:// (g.page/r/…/review) is the same link.
      const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(raw) ? raw : 'https://' + raw), host = url.hostname.toLowerCase(), on = d => host === d || host.endsWith('.' + d);
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.port) return '';
      if (!host.includes('.') || /^\d+(\.\d+){3}$/.test(host) || /(^|\.)(localhost|local|internal|test|invalid)$/.test(host)) return '';
      // Google: g.page/r/…, search.google.com/local/writereview?placeid=…, maps.app.goo.gl/…, google.com review pages and
      // its business-profile share links (g.co, share.google).
      // A Google link that opens no review page (a search, Google's home page) is not one (PocketCore.googleReview).
      const hosts = core().REVIEW_HOSTS[platform];
      if (platform === 'Google' && core().googleReview && !core().googleReview(url.href)) return '';
      return !hosts || hosts.some(on) ? url.href : '';
    } catch (_) { return ''; }
  }
  // The link each card type opens (pocket.js LINKED): a Website card with their website, or a Social card with the
  // profile their site links, already has its link.
  const LINKED = Object.freeze({ Website: 'website link', Booking: 'booking link', Social: 'profile link', Other: 'link' });
  /** The gate's link rule (pocket.js linkGate): a card type that needs no link, or one whose link a card can open. */
  function gate(purpose, destination) { return !LINKED[purpose] || !!safe(destination); }
  // Owner's rule (Bryton, Oct 26, 11:28 PM; LINK-FALLBACK-1, Oct 30): a review card without its review link is their
  // Website card on their own website, when the website is a link a card can open; with no website it waits for the link.
  /** The website a review card opens without its review link (pocket.js reviewSite), else ''. */
  function fallback(purpose, destination, website) {
    if (purpose !== 'Reviews' || safe(destination)) return '';
    const site = website || '';
    return safe(site) ? site : '';
  }
  // A Google listing (a Maps or share link) opens their listing, not the review form: taken, and checked by hand
  // (review_link_check), so Buy waits for the review link (pocket.js listingLink, for a review card).
  /** A Google review card's link that is their listing, not their review form. */
  function listing(link, platform) { return platform === 'Google' && core().googleReview?.(link) === 'listing'; }
  /** What the gate says when a card's link is missing (pocket.js gateWords for 'link'), long or short (the bar's). */
  function words(purpose, { platform = '', listing: isListing = false, short = false } = {}) {
    if (isListing) return short ? 'Paste your review form link to order.' : 'That’s your Google listing, not your review form. Paste your review link to order: your cards open it.';
    const what = purpose === 'Reviews' ? (platform === 'Google' ? 'Google review link' : 'review link') : purpose === 'Contact' ? 'contact details' : LINKED[purpose] || 'link';
    return short ? 'Add your ' + what + ' to order.' : 'Add your ' + what + ' to order: your cards open ' + (purpose === 'Contact' ? 'them' : 'it') + '.';
  }
  // What a tap still needs, by card type (pocket.js LINK_NOTES): a card type without one never waits for a link.
  const NEEDS = Object.freeze({ Reviews: 'review_link_needed', Website: 'website_link_needed', Booking: 'booking_link_needed', Social: 'profile_link_needed', Other: 'link_needed', Contact: 'contact_details_needed' });

  /**
   * The gate's answer for one card's link, as assets/pocket.js works it out (linkNote: linkGate, validDestination, the
   * review card's website fallback and Google's listing link), from what the shopper gave instead of the page's fields.
   * @param {string} url - The link field's value for this card type (a review card: its review link field)
   * @param {string} purpose - Reviews, Website, Booking, Social, Other or Contact
   * @param {object} [ctx] - platform: a review card's review site ('Google', 'Yelp', 'Trustpilot'; Google by default);
   *   website: the business's website (their lookup's); social: the profile their site links for the chosen platform;
   *   reviewDestination: a review card's "Preferred platform (or link)" (review site Other), while it shows;
   *   contact: a contact card's {first, phone, email, website}
   * @returns {{ok: boolean, url: string, reason: string, words: string, opensWebsite: boolean}} ok: the gate takes it;
   *   url: what a tap opens, as the order line's Destination carries it ('' when it is not taken); reason: '' (taken), 'website'
   *   (taken: a review card opening their website), 'listing', 'invalid', 'empty' or 'details' (a contact card); words:
   *   the gate's own words when it is not taken.
   */
  function check(url, purpose, ctx = {}) {
    const p = String(purpose || ''), platform = ctx.platform || 'Google', value = String(url ?? ''), trimmed = value.trim();
    const answer = (ok, link, reason, extra) => ({ ok, url: link, reason, words: ok ? '' : words(p, { platform, listing: reason === 'listing' }), opensWebsite: false, ...extra });
    if (p === 'Reviews') {
      const link = reviewUrl(value, platform);
      if (link && listing(link, platform)) return answer(false, '', 'listing');
      // destination(): the review link field's link (reviewLinkValue); without one, what they typed for another review
      // site (its "Preferred platform (or link)" field, while it shows), else the review site's name, never a link.
      const other = String(ctx.reviewDestination ?? '').trim();
      const destination = link || (other ? safe(other) || other : platform);
      if (safe(destination)) return answer(true, destination, '');
      const site = fallback(p, destination, ctx.website);
      if (site) return answer(true, site, 'website', { opensWebsite: true });
      return answer(false, '', trimmed ? 'invalid' : 'empty');
    }
    // destination(): what they typed (safeLink of it, else as typed), else what the business already has for this type.
    const typed = trimmed ? safe(trimmed) || trimmed : '';
    const destination = typed || (p === 'Website' ? String(ctx.website || '') : p === 'Social' ? String(ctx.social || '') : '');
    // validDestination(): every one of its checks (its field's value as typed, as the page reads it).
    const contact = ctx.contact || {};
    const valid = !(p === 'Other' && !safe(value))
      && !(trimmed && !safe(value))
      && !(p === 'Contact' && !safe(value) && (!String(contact.first || '').trim() || ![contact.phone, contact.email, contact.website].some(v => String(v || '').trim())))
      && !(p === 'Website' && !destination);
    if ((gate(p, destination) && valid) || !NEEDS[p]) return answer(true, destination, '');
    return answer(false, '', trimmed ? 'invalid' : p === 'Contact' ? 'details' : 'empty');
  }

  /* ---------- The "Check the link" part ---------- */
  const QR_FROM = '(min-width: 1024px)', QR_SEEN_MS = 2000;
  const BLOCK = '[data-pf-link-check]';
  let doc = null, wide = null;
  const states = new WeakMap();
  const hostOf = link => { try { return new URL(link).hostname.replace(/^www\./i, ''); } catch (_) { return ''; } };
  const httpLink = link => /^https?:\/\//i.test(String(link || ''));
  // As assets/pocket-cro.js sends its pfx_* events (PostHog, with the flow and its layer); never the link itself.
  function capture(name, props) {
    try { root.posthog?.capture(name, { flow: doc?.documentElement.dataset.pfFlow || 'classic', layer: 'pfx-v1', ...props }); } catch (_) {}
  }
  function propsOf(block) {
    const d = block.dataset;
    return {
      url: d.url || '', purpose: d.purpose || '', placeName: d.place || '', website: d.website || '', platform: d.platform || '',
      social: d.social || '', reviewDestination: d.pflcReviewDestination || '', source: d.source || '',
    };
  }

  /**
   * Draws one block for a card's link, from its data-* or from props (which it keeps there for the next draw).
   * @param {HTMLElement} block - A [data-pf-link-check] block (snippets/pf-link-check.liquid)
   * @param {object} [props] - url (the order line's Destination, '' for none), purpose, placeName (a review card's matched
   *   place), website (their website, for a review card without its review link), platform, social, reviewDestination,
   *   contact, source
   * @returns {object|null} check()'s answer
   */
  function render(block, props) {
    if (!block?.matches?.(BLOCK)) return null;
    // A review card's other site is kept as data-pflc-review-destination, never data-review-destination: pocket.js writes
    // the old review dialog's destination line into every [data-review-destination] on the page (CONSOL-1b).
    const keep = { url: 'url', purpose: 'purpose', placeName: 'place', website: 'website', platform: 'platform', social: 'social', reviewDestination: 'pflcReviewDestination', source: 'source' };
    if (props) for (const [key, attr] of Object.entries(keep)) if (props[key] !== undefined) block.dataset[attr] = String(props[key] ?? '');
    const now = { ...propsOf(block), ...(props?.contact ? { contact: props.contact } : {}) };
    const result = check(now.url, now.purpose, { platform: now.platform || undefined, website: now.website, social: now.social, reviewDestination: now.reviewDestination, contact: now.contact });
    // What it shows, byte for byte: the order line's Destination as given, or their website a review card opens without
    // its review link. Never a link a tap can't open (no javascript: or other address in Test link).
    const shown = result.opensWebsite ? result.url : now.url;
    const ok = result.ok && httpLink(shown);
    const part = selector => block.querySelector(selector);
    const okPart = part('[data-pflc-ok]'), fixPart = part('[data-pflc-fix]');
    if (okPart) okPart.hidden = !ok;
    if (fixPart) fixPart.hidden = ok && !result.opensWebsite;
    const host = part('[data-pflc-host]'), line = part('[data-pflc-url]'), test = part('[data-pflc-test]');
    if (host) host.textContent = ok ? hostOf(shown) : '';
    if (line) line.textContent = ok ? shown : '';
    if (test) { if (ok) test.setAttribute('href', shown); else test.removeAttribute('href'); }
    const place = part('[data-pflc-place]');
    if (place) {
      const name = place.querySelector('b') || place;
      if (name !== place) name.textContent = now.placeName;
      place.hidden = !(ok && !result.opensWebsite && now.purpose === 'Reviews' && now.placeName);
    }
    const note = part('[data-pflc-note]');
    if (note) {
      // Its words name Google only for a Google review card: another site's says "No review link yet" (CONSOL-1b).
      if (!('google' in note.dataset)) note.dataset.google = note.textContent;
      const said = (now.platform || 'Google') === 'Google' ? note.dataset.google : note.dataset.other || note.dataset.google;
      if (note.textContent !== said) note.textContent = said;
      note.hidden = !(ok && result.opensWebsite);
    }
    const said = part('[data-pflc-words]');
    if (said) { said.textContent = ok ? '' : result.words || words(now.purpose, { platform: now.platform || 'Google' }); said.hidden = !said.textContent; }
    const button = part('[data-pflc-fix-button]');
    // Its words only: the icon beside them stays (a block drawn before it had one has its words as the button's text).
    if (button) (button.querySelector('[data-pflc-fix-words]') || button).textContent = result.opensWebsite ? button.dataset.reviewLabel || 'Add your Google review link' : button.dataset.label || 'Add the link your cards open';
    block.dataset.state = ok ? (result.opensWebsite ? 'website' : 'ok') : result.reason || 'invalid';
    // Fixed: a block that had no link the gate takes, given one after its fix button was pressed. The Link step's block
    // is drawn on every design change from the page's first lookup on, unseen until it has a link (pocket-card-profile.js
    // paintCheck), so a link the lookup brought is not a fix (p2-review-1a2-browser, Nov 1).
    const before = states.get(block);
    if (before && before.asked && !before.ok && ok && !result.opensWebsite) capture('review_link_fixed', { purpose: now.purpose, source: now.source });
    states.set(block, { ok: ok && !result.opensWebsite, asked: !!before?.asked && !(ok && !result.opensWebsite), shown: ok ? shown : '', qr: before?.shown === shown ? before.qr : null, seen: before?.shown === shown ? before.seen : false });
    paintQr(block);
    return result;
  }

  /* The QR: from 1024px only (a phone is the thing that scans it), drawn once for each link, static. */
  async function paintQr(block) {
    const figure = block.querySelector('[data-pflc-qr]'), slot = block.querySelector('[data-pflc-qr-code]'), state = states.get(block);
    if (!figure || !slot || !state) return;
    const show = !!state.shown && !!wide?.matches;
    if (!show) { figure.hidden = true; return; }
    if (state.qr !== state.shown) {
      const link = state.shown, svg = await root.PocketQR?.svg?.(link).catch?.(() => null);
      if (states.get(block)?.shown !== link) return;
      if (!svg) { figure.hidden = true; return; }
      slot.innerHTML = svg;
      const drawn = slot.querySelector('svg');
      if (drawn) {
        drawn.setAttribute('role', 'img'); drawn.setAttribute('aria-label', 'QR code for ' + link); drawn.removeAttribute('width'); drawn.removeAttribute('height');
        // Small, but never under 3px a module (a long link's code is denser), so a phone reads it from any screen.
        const modules = Number(String(drawn.getAttribute('viewBox') || '').split(' ')[2]) || 0;
        figure.style.setProperty('--pflc-qr', Math.max(112, Math.ceil(modules * 3)) + 'px');
      }
      state.qr = link;
    }
    figure.hidden = !(state.shown && wide?.matches);
    watchQr(block);
  }
  // review_link_tested {method: qr}: the QR on screen for 2 s, once for each link (a scan can't be seen from here).
  const seenTimers = new WeakMap();
  let seenObserver = null;
  function watchQr(block) {
    const figure = block.querySelector('[data-pflc-qr]');
    if (!figure || typeof root.IntersectionObserver !== 'function') return;
    seenObserver ||= new root.IntersectionObserver(entries => {
      for (const entry of entries) {
        const owner = entry.target.closest(BLOCK), state = owner && states.get(owner);
        if (!state) continue;
        root.clearTimeout(seenTimers.get(owner));
        if (!entry.isIntersecting || state.seen || entry.target.hidden) continue;
        seenTimers.set(owner, root.setTimeout(() => {
          const s = states.get(owner);
          if (!s || s.seen || figure.hidden) return;
          s.seen = true;
          capture('review_link_tested', { purpose: owner.dataset.purpose || '', method: 'qr', source: owner.dataset.source || '' });
        }, QR_SEEN_MS));
      }
    }, { threshold: 0.6 });
    seenObserver.observe(figure);
  }

  function wire(block) {
    if (block.dataset.pflcWired) return;
    block.dataset.pflcWired = '';
    block.addEventListener('click', event => {
      const test = event.target.closest?.('[data-pflc-test]');
      if (test && block.contains(test)) {
        if (!test.getAttribute('href')) { event.preventDefault(); return; }
        capture('review_link_tested', { purpose: block.dataset.purpose || '', method: 'open', source: block.dataset.source || '' });
        return;
      }
      const fix = event.target.closest?.('[data-pflc-fix-button]');
      if (fix && block.contains(fix)) {
        const state = block.dataset.state || '';
        // Asked for: a link given from now on is their fix (review_link_fixed), never one a lookup filled in by itself.
        const was = states.get(block);
        if (was) was.asked = true;
        block.dispatchEvent(new root.CustomEvent('pf:link-fix', { bubbles: true, composed: true, detail: { purpose: block.dataset.purpose || '', reason: state === 'website' ? 'website' : state, source: block.dataset.source || '' } }));
      }
    });
  }
  /** Draws every block inside a container (the page by default): a host that adds one later calls it, or it is found. */
  function mount(container) {
    const scope = container || doc;
    if (!scope?.querySelectorAll) return;
    const blocks = scope.matches?.(BLOCK) ? [scope] : [...scope.querySelectorAll(BLOCK)];
    for (const block of blocks) { wire(block); render(block); }
  }
  let started = false;
  function start(d) {
    if (started || !d) return;
    started = true;
    doc = d;
    wide = root.matchMedia?.(QR_FROM) || null;
    wide?.addEventListener?.('change', () => { for (const block of doc.querySelectorAll(BLOCK)) paintQr(block); });
    const go = () => {
      mount(doc);
      if (typeof root.MutationObserver === 'function') {
        new root.MutationObserver(records => {
          for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1 && (node.matches(BLOCK) || node.querySelector(BLOCK))) mount(node);
        }).observe(doc.documentElement, { childList: true, subtree: true });
      }
    };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', go, { once: true });
    else go();
  }

  return Object.freeze({ version: 1, LINKED, NEEDS, safe, reviewUrl, gate, fallback, listing, words, check, render, mount, start });
});
